import { useQueryClient } from '@tanstack/react-query'
import { useAppMutation } from '@/helpers/api/mutation'

import { createInstance } from '@/helpers/api/base'
import { Log } from '@/types/logs'
import { useNotification } from '@/ui/Notification'
import { useGetUserInfo } from '@/modules/Auth'
import { useModal } from '@/components/Modal'
import { GET_FLAGGED_LOGS_KEY } from '@/pages/UnfinishedRecords/api/useGetFlaggedLogs'

import styles from '../ui/components/information/Unfinished.module.scss'

import FlagIcon from '../assets/flag.svg'
import DangerFlagIcon from '../assets/danger-flag.svg'

interface IFlagLogUnfinishedRequest {
    isFlagged: boolean
    comment: string
    author: string
}

export const useFlagLogUnfinished = (log?: Log) => {
    const queryClient = useQueryClient()
    const { data: userInfo } = useGetUserInfo()
    const { showNotification } = useNotification()
    const { closeModal } = useModal()

    const flagLogUnfinishedMutation = useAppMutation<Log, Error, IFlagLogUnfinishedRequest>({
        mutationFn: (data) =>
            createInstance({
                url: `/controller-logs/${log?.id}/flag-unfinished`,
                method: 'PATCH',
                data,
            }),
        alertOnError: true,
        onError: () => closeModal(),
        onSuccess: (updatedLog) => {
            const updater = (prev: Log[] | undefined) => prev?.map((l) => (l.id === updatedLog.id ? updatedLog : l))
            queryClient.setQueriesData<Log[]>({ queryKey: ['controllerLogs'] }, updater)
            queryClient.setQueriesData<Log[]>({ queryKey: [GET_FLAGGED_LOGS_KEY] }, updater)
            closeModal()
            showNotification({
                className: styles.flaggedNotification,
                message: (
                    <>
                        {log?.unfinishedFlagMetadata?.flag ? (
                            <>
                                <FlagIcon />
                                <p>Flag removed</p>
                            </>
                        ) : (
                            <>
                                <DangerFlagIcon />
                                <p>Record flagged</p>
                            </>
                        )}
                    </>
                ),
            })
        },
    })

    const handleFlagLogUnfinished = (comment: string) => {
        flagLogUnfinishedMutation.mutate({
            isFlagged: true,
            author: userInfo?.lastSessionDetails.authorName || '',
            comment,
        })
    }

    const handleRemoveLogUnfinishedFlag = () => {
        flagLogUnfinishedMutation.mutate({
            isFlagged: false,
            author: userInfo?.lastSessionDetails.authorName || '',
            comment: log?.unfinishedFlagMetadata?.comment || '',
        })
    }

    return {
        handleFlagLogUnfinished,
        handleRemoveLogUnfinishedFlag,
        isPending: flagLogUnfinishedMutation.isPending,
    }
}
